import { AxiosResponse, HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  CreateTrailerRequest,
  CreateTruckRequest,
  DocumentObjectType,
  DocumentUploadPresignedUrlsRequest,
  PatchTrailerRequest,
  PatchTruckRequest,
  PresignedUrlObject,
  TrailerDocumentSubType,
  TruckDocumentSubType,
} from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { getFileExtension } from '../../../../../../../utils/FileUtils';
import { parseCommaNumber } from '../../../../../../../hooks/useInputFormat';
import {
  AssetFormData,
  AssetFormMode,
  AssetType,
  AssetView,
  VinFields,
} from './types';

const ASSETS_ROUTE = '/dashboard/assets';

type UseAssetMutationsArgs = {
  mode: AssetFormMode;
  customerId: string;
  /** Edit only — supplies truck_id / trailer_id for the PATCH URL. */
  selectedAsset?: AssetView | null;
  /** Create only — decoded VIN values (make/model/year/class). */
  vinFields: VinFields;
  assetImage: File | null;
  /** Edit only — current show/hide + removal state. */
  showImage: boolean;
  imageRemoved: boolean;
  setAssetImageUrl?: (url: string) => void;
  setLoading: (loading: boolean) => void;
};

/**
 * Single source of truth for asset create/edit submission. Branches on
 * `mode` (POST create / PATCH edit) and asset type (`/trucks` vs `/trailers`)
 * and reproduces the four legacy payloads byte-for-byte, including the
 * deliberate quirks recorded in docs/spec-assetform-decomposition-ui.md:
 *
 *   - `coerceOwnedPays` is CREATE-ONLY (owned => paysForPMSRepairs: true).
 *   - truck payment fields use `parseCommaNumber`; trailer uses `Number`.
 *   - date fields: create sends `|| undefined` (omit), edit sends `?? ''`.
 *   - status acceptance: create truck/trailer + patch truck accept 200|201;
 *     patch trailer accepts 200 ONLY (legacy inconsistency, preserved).
 *   - VIN/make/model/year/class: create only. `show_asset_image`: edit only.
 */
export function useAssetMutations({
  mode,
  customerId,
  selectedAsset,
  vinFields,
  assetImage,
  showImage,
  imageRemoved,
  setAssetImageUrl,
  setLoading,
}: UseAssetMutationsArgs) {
  const { callProtectedApi, callApi } = useApi();
  const navigate = useNavigate();

  // create truck/trailer + patch truck accept 200 OR 201.
  const createSucceeded = (response: AxiosResponse<any> | null) =>
    response?.status === HttpStatusCode.Ok ||
    response?.status === HttpStatusCode.Created;
  // patch trailer accepts 200 ONLY (legacy behavior — do not "fix" silently).
  const okOnly = (response: AxiosResponse<any> | null) =>
    response?.status === HttpStatusCode.Ok;

  // Create-only: an "owned" asset always pays for PM/repairs.
  const coerceOwnedPays = (info: AssetFormData): AssetFormData =>
    info.ownershipStatus === 'owned'
      ? { ...info, paysForPMSRepairs: true }
      : info;

  const uploadAssetImage = async (
    data: any,
    assetType: AssetType,
    image: File,
    fileExtension: string
  ) => {
    switch (assetType) {
      case 'trailer': {
        const responseDocUrl = await callProtectedApi(
          `/documents/${DocumentObjectType.Trailer}/${data?.trailer_id}`,
          'post',
          {
            document_count: 1,
            document_sub_type: TrailerDocumentSubType.Images,
            file_extensions: [fileExtension],
          } as DocumentUploadPresignedUrlsRequest,
          'Error occurred while uploading your trailer asset image.'
        );
        const url: PresignedUrlObject[] =
          responseDocUrl?.data?.data?.document_urls;
        callApi(
          '',
          'put',
          image,
          'Error occurred while uploading trailer image.',
          { 'Content-Type': image.type },
          url[0]?.presignedUrl,
          5
        );
        break;
      }
      case 'truck': {
        const responseDocUrlTruck = await callProtectedApi(
          `/documents/${DocumentObjectType.Truck}/${data?.truck_id}`,
          'post',
          {
            document_count: 1,
            document_sub_type: TruckDocumentSubType.Images,
            file_extensions: [fileExtension],
          } as DocumentUploadPresignedUrlsRequest,
          'Error occurred while uploading your trailer asset image.'
        );
        const urlTruck = responseDocUrlTruck?.data?.data?.document_urls;
        callApi(
          '',
          'put',
          image,
          'Error occurred while uploading truck image.',
          { 'Content-Type': image.type },
          urlTruck[0]?.presignedUrl,
          5
        );
        break;
      }
    }
  };

  const maybeUploadImage = async (data: any, assetType: AssetType) => {
    if (assetImage) {
      const fileExtension = getFileExtension(assetImage);
      if (fileExtension) {
        await uploadAssetImage(data, assetType, assetImage, fileExtension);
      }
    }
  };

  // ---------------- CREATE ----------------
  const createTrailer = async (assetInfo: AssetFormData) => {
    setLoading(true);
    try {
      const safe = coerceOwnedPays(assetInfo);
      const response = await callProtectedApi(
        '/trailers',
        'post',
        {
          customer_id: customerId,
          vehicle_id_number: safe.vinNumber,
          ownership_status: safe?.ownershipStatus,
          pay_for_repairs: safe?.paysForPMSRepairs,
          tire_brand_quality: safe?.tireBrandQuality,
          trailer_display_name: safe?.assetDisplayName,
          trailer_make: vinFields?.make,
          trailer_model: vinFields?.model,
          trailer_year: Number(vinFields?.year),
          trailer_payment_amount: Number(safe?.assetPayment),
          insurance_payment_amount: Number(safe?.insurancePayment),
        } as CreateTrailerRequest,
        'Error occurred while creating your trailer asset.'
      );
      const data = response?.data?.data?.trailer;
      if (data && Object.entries(data)?.length > 0 && createSucceeded(response)) {
        await maybeUploadImage(data, 'trailer');
        navigate(ASSETS_ROUTE);
        toast.success('Trailer created successfully!');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating your new trailer asset: ',
        e?.message
      );
    } finally {
      setLoading(false);
    }
  };

  const createTruck = async (assetInfo: AssetFormData) => {
    setLoading(true);
    try {
      const safe = coerceOwnedPays(assetInfo);
      const response = await callProtectedApi(
        '/trucks',
        'post',
        {
          customer_id: customerId,
          vehicle_id_number: safe.vinNumber,
          ownership_status: safe?.ownershipStatus,
          starting_mileage: parseCommaNumber(safe?.assetMileage),
          typical_annual_mileage: parseCommaNumber(safe?.typicalAnnualMileage),
          is_out_of_service: safe?.status === 'out_of_commission',
          pay_for_repairs: safe?.paysForPMSRepairs,
          tire_brand_quality: safe?.tireBrandQuality,
          truck_display_name: safe?.assetDisplayName,
          out_of_service_reason:
            safe?.status === 'out_of_commission'
              ? safe?.outOfServiceReason
              : undefined,
          engine_condition: safe?.condition,
          truck_make: vinFields?.make,
          truck_model: vinFields?.model,
          truck_year: Number(vinFields?.year),
          truck_class_number: vinFields?.class,
          truck_payment_amount: parseCommaNumber(safe?.assetPayment),
          insurance_payment_amount: parseCommaNumber(safe?.insurancePayment),
          date_of_last_pm: safe?.dateOfLastPM || undefined,
          date_of_last_repair: safe?.dateOfLastRepair || undefined,
        } as CreateTruckRequest,
        'Error occurred while creating your truck asset.'
      );
      const data = response?.data?.data?.truck;
      if (data && Object.entries(data)?.length > 0 && createSucceeded(response)) {
        await maybeUploadImage(data, 'truck');
        navigate(ASSETS_ROUTE);
        toast.success('Truck created successfully!');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating your new truck asset: ',
        e?.message
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------- EDIT ----------------
  const patchTrailer = async (assetInfo: AssetFormData) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/trailers/' + selectedAsset?.trailer_id,
        'patch',
        {
          customer_id: customerId,
          ownership_status: assetInfo?.ownershipStatus,
          pay_for_repairs: assetInfo?.paysForPMSRepairs,
          tire_brand_quality: assetInfo?.tireBrandQuality,
          trailer_display_name: assetInfo?.assetDisplayName,
          trailer_payment_amount: Number(assetInfo?.assetPayment),
          insurance_payment_amount: Number(assetInfo?.insurancePayment),
          show_asset_image: showImage,
        } as PatchTrailerRequest,
        'Error occurred while updating your trailer asset.'
      );
      const data = response?.data?.data?.trailer;
      // NOTE: patch trailer checks 200 ONLY (legacy).
      if (data && Object.entries(data)?.length > 0 && okOnly(response)) {
        await maybeUploadImage(data, 'trailer');
        // No dedicated image-removal API: clear locally when flagged.
        if (imageRemoved && !assetImage) {
          setAssetImageUrl?.('');
        }
        navigate(ASSETS_ROUTE);
        toast.success('Trailer updated successfully!');
      }
    } catch (e: any) {
      console.error('Error occurred while updating your trailer asset: ', e);
    } finally {
      setLoading(false);
    }
  };

  const patchTruck = async (assetInfo: AssetFormData) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/trucks/' + selectedAsset?.truck_id,
        'patch',
        {
          customer_id: customerId,
          ownership_status: assetInfo?.ownershipStatus,
          starting_mileage: parseCommaNumber(assetInfo?.assetMileage),
          typical_annual_mileage: parseCommaNumber(
            assetInfo?.typicalAnnualMileage
          ),
          is_out_of_service: assetInfo?.status === 'out_of_commission',
          pay_for_repairs: assetInfo?.paysForPMSRepairs,
          tire_brand_quality: assetInfo?.tireBrandQuality,
          truck_display_name: assetInfo?.assetDisplayName,
          out_of_service_reason:
            assetInfo?.status === 'out_of_commission'
              ? assetInfo?.outOfServiceReason
              : undefined,
          truck_payment_amount: parseCommaNumber(assetInfo?.assetPayment),
          insurance_payment_amount: parseCommaNumber(assetInfo?.insurancePayment),
          engine_condition: assetInfo?.condition,
          show_asset_image: showImage,
          date_of_last_pm: assetInfo?.dateOfLastPM ?? '',
          date_of_last_repair: assetInfo?.dateOfLastRepair ?? '',
        } as PatchTruckRequest,
        'Error occurred while updating your truck asset.'
      );
      const data = response?.data?.data?.truck;
      // NOTE: patch truck accepts 200 OR 201 (legacy).
      if (data && Object.entries(data)?.length > 0 && createSucceeded(response)) {
        await maybeUploadImage(data, 'truck');
        if (imageRemoved && !assetImage) {
          setAssetImageUrl?.('');
        }
        navigate(ASSETS_ROUTE);
        toast.success('Truck updated successfully!');
      }
    } catch (e: any) {
      console.error('Error occurred while updating your new truck asset: ', e);
    } finally {
      setLoading(false);
    }
  };

  const submit = (data: AssetFormData) => {
    if (mode === 'create') {
      return data.assetType === 'truck' ? createTruck(data) : createTrailer(data);
    }
    return data.assetType === 'truck' ? patchTruck(data) : patchTrailer(data);
  };

  return { submit };
}
